import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import Suggestion, { type SuggestionOptions } from '@tiptap/suggestion';

import { type Skill } from '../../skills';
import { createSkillCommandInsertNodes } from './SkillCommandNode';

export const SLASH_COMMAND_SUGGESTION_PLUGIN_KEY = new PluginKey('slashCommandSuggestion');

const SLASH_AUTO_FORMAT_PLUGIN_KEY = new PluginKey('slashAutoFormat');

// Matches a typed slash command followed by whitespace, e.g. "/remove-background "
export const SLASH_COMMAND_RE = /\/([a-z][a-z0-9-]*)\s$/;

export type SlashCommandSuggestionExtensionOptions = {
  suggestion: Omit<SuggestionOptions<Skill, Skill>, 'editor'>;
  // Uses a function not a ref to survive TipTap's mergeDeep option cloning.
  getSkills: () => Skill[];
  /** Called when a typed slash command matches a known skill. */
  onSkillMatch: (skill: Skill) => void;
};

export const SlashCommandSuggestion = Extension.create<SlashCommandSuggestionExtensionOptions>({
  addOptions() {
    return {
      getSkills: (): Skill[] => [],
      onSkillMatch: () => {},
      suggestion: {
        allowedPrefixes: null,
        allowSpaces: false,
        char: '/',
        pluginKey: SLASH_COMMAND_SUGGESTION_PLUGIN_KEY,
        startOfLine: false,
      },
    };
  },

  addProseMirrorPlugins() {
    const { getSkills, onSkillMatch } = this.options;

    const autoFormatPlugin = new Plugin({
      appendTransaction(transactions, _oldState, newState) {
        // Skip if this transaction was generated by this plugin (prevent loops)
        if (transactions.some((tr) => tr.getMeta(SLASH_AUTO_FORMAT_PLUGIN_KEY))) {
          return null;
        }
        // Only act on doc changes (not cursor moves)
        if (!transactions.some((tr) => tr.docChanged)) {
          return null;
        }

        const { selection } = newState;

        if (!selection.empty) {
          return null;
        }

        const { $from } = selection;
        const textBefore = newState.doc.textBetween($from.start(), $from.pos, undefined, '\ufffc');
        const match = textBefore.match(SLASH_COMMAND_RE);

        if (!match) {
          return null;
        }

        const skill = getSkills().find((s) => s.slashCommand === match[1]);

        if (!skill) {
          return null;
        }

        const insertNodes = createSkillCommandInsertNodes(newState.schema, skill.slashCommand);

        if (insertNodes.length === 0) {
          return null;
        }

        const matchStart = $from.pos - match[0].length;
        const matchEnd = $from.pos;

        const tr = newState.tr
          .replaceWith(matchStart, matchEnd, insertNodes)
          .setMeta(SLASH_AUTO_FORMAT_PLUGIN_KEY, true);

        // Defer side effect so it runs after the transaction is committed
        void Promise.resolve().then(() => onSkillMatch(skill));

        return tr;
      },
      key: SLASH_AUTO_FORMAT_PLUGIN_KEY,
    });

    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
      autoFormatPlugin,
    ];
  },
  name: 'slashCommandSuggestion',
});
